import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface RecordingCountdownProps {
  /** Seconds left (3, 2, 1…). */
  seconds: number;
  onCancel: () => void;
}

/** Full-area 3-2-1 before recording starts. Esc (handled by the recorder) or Cancel stops it. */
const RecordingCountdown: React.FC<RecordingCountdownProps> = ({ seconds, onCancel }) => {
  const { t } = useTranslation();
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => { cancelRef.current?.focus(); }, []);

  return (
    // Rendered inside the recorder's layer (not portaled), above its panels.
    <div
      role="alertdialog"
      aria-label={t('countdown_starting', 'Recording starts in…')}
      style={{
        position: 'fixed', inset: 0, zIndex: 2100,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 20,
        background: 'rgba(0, 0, 0, 0.55)', color: '#fff',
      }}
    >
      <p style={{ margin: 0, fontSize: 16, fontWeight: 500, opacity: 0.9 }}>
        {t('countdown_starting', 'Recording starts in…')}
      </p>
      <div
        key={seconds}
        aria-live="assertive"
        className="airec-countdown-number"
        style={{
          width: 140, height: 140, borderRadius: '50%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(220, 38, 38, 0.9)', boxShadow: '0 10px 40px rgba(0, 0, 0, 0.35)',
          fontSize: 72, fontWeight: 700, lineHeight: 1,
        }}
      >
        {seconds}
      </div>
      <button
        ref={cancelRef}
        type="button"
        onClick={onCancel}
        style={{
          padding: '8px 20px', borderRadius: 999, fontSize: 14, fontWeight: 500, cursor: 'pointer',
          background: 'rgba(255, 255, 255, 0.15)', color: '#fff', border: '1px solid rgba(255, 255, 255, 0.4)',
        }}
      >
        {t('countdown_cancel', 'Cancel (Esc)')}
      </button>
    </div>
  );
};

export default RecordingCountdown;
